import React, { useState, useEffect } from 'react';
import api from '../../../services/api';
import { useAuth } from '../../../stores/authStore';
import { AdminPageHeader } from '../components/AdminPageHeader';

const SvgIcon: React.FC<{ size?: number; className?: string; style?: React.CSSProperties; children: React.ReactNode }> = ({ size = 18, className, style, children }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} style={style}>
    {children}
  </svg>
);

const Cloud = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" /></SvgIcon>
);

const HardDrive = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><line x1="22" x2="2" y1="12" y2="12" /><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z" /><line x1="6" x2="6.01" y1="16" y2="16" /><line x1="10" x2="10.01" y1="16" y2="16" /></SvgIcon>
);

const CheckCircle2 = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><circle cx="12" cy="12" r="10" /><path d="m9 12 2 2 4-4" /></SvgIcon>
);

const AlertCircle = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><circle cx="12" cy="12" r="10" /><line x1="12" x2="12" y1="8" y2="12" /><line x1="12" x2="12.01" y1="16" y2="16" /></SvgIcon>
);

const RefreshCw = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" /><path d="M21 3v5h-5" /><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" /><path d="M8 16H3v5" /></SvgIcon>
);

const Key = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><circle cx="7.5" cy="15.5" r="5.5" /><path d="m21 2-9.6 9.6" /><path d="m15.5 7.5 3 3L22 7l-3-3" /></SvgIcon>
);

const ShieldCheck = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z" /><path d="m9 12 2 2 4-4" /></SvgIcon>
);

const Eye = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" /><circle cx="12" cy="12" r="3" /></SvgIcon>
);

const EyeOff = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><path d="M9.88 9.88a3 3 0 1 0 4.24 4.24" /><path d="M10.73 5.08A10.43 10.43 0 0 1 12 5c7 0 10 7 10 7a13.16 13.16 0 0 1-1.67 2.68" /><path d="M6.61 6.61A13.526 13.526 0 0 0 2 12s3 7 10 7a9.74 9.74 0 0 0 5.39-1.61" /><line x1="2" x2="22" y1="2" y2="22" /></SvgIcon>
);

const Server = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><rect width="20" height="8" x="2" y="2" rx="2" ry="2" /><rect width="20" height="8" x="2" y="14" rx="2" ry="2" /><line x1="6" x2="6.01" y1="6" y2="6" /><line x1="6" x2="6.01" y1="18" y2="18" /></SvgIcon>
);

const Database = (props: { size?: number; style?: React.CSSProperties; className?: string }) => (
  <SvgIcon {...props}><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5V19A9 3 0 0 0 21 19V5" /><path d="M3 12A9 3 0 0 0 21 12" /></SvgIcon>
);

interface StorageConfigData {
  tenantId: string;
  provider: 's3' | 'local' | 'cloudinary';
  s3Bucket: string;
  s3Region: string;
  s3CustomPrefix: string;
  isUsingEnvDefault: boolean;
  keysConfigured: {
    awsAccessKeyId: boolean;
    awsAccessKeyIdMasked?: string;
    awsSecretAccessKey: boolean;
    awsSecretAccessKeyMasked?: string;
  };
  updatedAt?: string;
  updatedBy?: string;
}

interface TestResult {
  status: 'idle' | 'testing' | 'success' | 'error';
  latencyMs?: number;
  message?: string;
  error?: string;
}

const AWS_REGIONS = [
  { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney) — ap-southeast-2 [Recommended]' },
  { value: 'ap-south-1', label: 'Asia Pacific (Mumbai) — ap-south-1' },
  { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore) — ap-southeast-1' },
  { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo) — ap-northeast-1' },
  { value: 'us-east-1', label: 'US East (N. Virginia) — us-east-1' },
  { value: 'us-east-2', label: 'US East (Ohio) — us-east-2' },
  { value: 'us-west-2', label: 'US West (Oregon) — us-west-2' },
  { value: 'eu-west-1', label: 'Europe (Ireland) — eu-west-1' },
  { value: 'eu-central-1', label: 'Europe (Frankfurt) — eu-central-1' },
  { value: 'me-south-1', label: 'Middle East (Bahrain) — me-south-1' }
];

export function TenantStorageSettingsPage() {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === 'PLATFORM_ADMIN' || user?.role === 'SUPER_ADMIN' || user?.role === 'PLATFORM_SUPER_ADMIN';

  const [tenantsList, setTenantsList] = useState<Array<{ id: string; name: string; slug: string }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>(user?.tenantId || '');

  const [config, setConfig] = useState<StorageConfigData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Form Fields
  const [provider, setProvider] = useState<'s3' | 'local' | 'cloudinary'>('s3');
  const [s3Bucket, setS3Bucket] = useState('trainova-media-vault');
  const [s3Region, setS3Region] = useState('ap-southeast-2');
  const [s3CustomPrefix, setS3CustomPrefix] = useState('');
  const [awsAccessKeyId, setAwsAccessKeyId] = useState('');
  const [awsSecretAccessKey, setAwsSecretAccessKey] = useState('');
  const [showSecretKey, setShowSecretKey] = useState(false);

  // Ping Diagnostic State
  const [testResult, setTestResult] = useState<TestResult>({ status: 'idle' });

  // Load Tenants for Super Admin
  useEffect(() => {
    if (isSuperAdmin) {
      api.admin.tenants.list().then((res: any) => {
        if (res?.success && Array.isArray(res.data?.tenants)) {
          setTenantsList(res.data.tenants);
          if (!selectedTenantId && res.data.tenants.length > 0) {
            setSelectedTenantId(res.data.tenants[0].id);
          }
        }
      }).catch(err => console.warn('Could not load tenants list:', err));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    fetchConfig();
  }, [selectedTenantId]);

  async function fetchConfig() {
    try {
      setIsLoading(true);
      setTestResult({ status: 'idle' });
      const targetTenant = selectedTenantId || user?.tenantId;
      const res: any = await api.admin.getStorageConfig(targetTenant);
      if (res && res.data) {
        const d: StorageConfigData = res.data;
        setConfig(d);
        setProvider(d.provider || 's3');
        setS3Bucket(d.s3Bucket || 'trainova-media-vault');
        setS3Region(d.s3Region || 'ap-southeast-2');
        setS3CustomPrefix(d.s3CustomPrefix || '');
        setAwsAccessKeyId(d.keysConfigured?.awsAccessKeyIdMasked || '');
        setAwsSecretAccessKey(d.keysConfigured?.awsSecretAccessKeyMasked || '');
      }
    } catch (err: any) {
      console.error('Failed to load storage config:', err);
      setFeedbackMessage({
        type: 'error',
        text: err?.message || 'Failed to load storage configuration.'
      });
    } finally {
      setIsLoading(false);
    }
  }

  async function handleTestConnection() {
    setTestResult({ status: 'testing' });
    try {
      const payload: any = {
        tenantId: selectedTenantId || user?.tenantId,
        provider,
        s3Bucket: s3Bucket.trim(),
        s3Region: s3Region.trim()
      };

      if (awsAccessKeyId && !awsAccessKeyId.includes('••••')) {
        payload.awsAccessKeyId = awsAccessKeyId.trim();
      }
      if (awsSecretAccessKey && !awsSecretAccessKey.includes('••••')) {
        payload.awsSecretAccessKey = awsSecretAccessKey.trim();
      }

      const res: any = await api.admin.testS3Connection(payload);
      if (res && res.success) {
        setTestResult({
          status: 'success',
          latencyMs: res.latencyMs,
          message: res.message || `Successfully connected to S3 bucket "${s3Bucket}" in region "${s3Region}".`
        });
      } else {
        setTestResult({
          status: 'error',
          error: res?.error || 'Connection check failed.'
        });
      }
    } catch (err: any) {
      setTestResult({
        status: 'error',
        error: err?.message || 'Failed to reach S3 bucket. Check bucket name and AWS IAM permissions.'
      });
    }
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setFeedbackMessage(null);

    try {
      const targetTenant = selectedTenantId || user?.tenantId;
      const payload: any = {
        provider,
        s3Bucket: s3Bucket.trim(),
        s3Region: s3Region.trim(),
        s3CustomPrefix: s3CustomPrefix.trim()
      };

      if (awsAccessKeyId && !awsAccessKeyId.includes('••••')) {
        payload.awsAccessKeyId = awsAccessKeyId.trim();
      }
      if (awsSecretAccessKey && !awsSecretAccessKey.includes('••••')) {
        payload.awsSecretAccessKey = awsSecretAccessKey.trim();
      }

      const res: any = await api.admin.updateStorageConfig(payload, targetTenant);
      if (res && (res.success || res.data)) {
        setFeedbackMessage({
          type: 'success',
          text: 'Storage configuration successfully saved and encrypted with AES-256-GCM.'
        });
        if (res.data) {
          setConfig(res.data);
          setAwsAccessKeyId(res.data.keysConfigured?.awsAccessKeyIdMasked || '');
          setAwsSecretAccessKey(res.data.keysConfigured?.awsSecretAccessKeyMasked || '');
        }
      } else {
        setFeedbackMessage({
          type: 'error',
          text: res?.message || 'Failed to update storage configuration.'
        });
      }
    } catch (err: any) {
      setFeedbackMessage({
        type: 'error',
        text: err?.message || 'Failed to update storage configuration.'
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div style={{ maxWidth: '1080px', margin: '0 auto', paddingBottom: '60px' }}>
      <AdminPageHeader
        title="Media & Storage Configuration"
        description="Configure cloud video recordings, AWS S3 storage vaults, and access credentials."
      />

      {/* Tenant Selector for Super Admin */}
      {isSuperAdmin && tenantsList.length > 0 && (
        <div style={{
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          padding: '16px 20px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '16px',
          boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Database size={18} style={{ color: 'var(--blue)' }} />
            <div>
              <div style={{ fontWeight: 600, fontSize: '14px', color: 'var(--ink)' }}>Target Organization / Tenant</div>
              <div style={{ fontSize: '12px', color: 'var(--muted)' }}>Select which organization's storage vault to configure</div>
            </div>
          </div>
          <select
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            style={{
              padding: '8px 14px',
              borderRadius: '8px',
              border: '1px solid var(--line)',
              background: 'var(--surface)',
              color: 'var(--ink)',
              fontSize: '14px',
              fontWeight: 500,
              minWidth: '220px'
            }}
          >
            {tenantsList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Feedback Alert */}
      {feedbackMessage && (
        <div style={{
          padding: '12px 18px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          background: feedbackMessage.type === 'success' ? 'var(--ok-soft)' : 'var(--bad-soft)',
          border: `1px solid ${feedbackMessage.type === 'success' ? 'rgba(15, 138, 95, 0.2)' : 'rgba(194, 59, 59, 0.2)'}`,
          color: feedbackMessage.type === 'success' ? 'var(--ok)' : 'var(--bad)',
          fontSize: '14px',
          fontWeight: 500
        }}>
          {feedbackMessage.type === 'success' ? <CheckCircle2 size={18} /> : <AlertCircle size={18} />}
          <span>{feedbackMessage.text}</span>
        </div>
      )}

      {isLoading ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          background: 'var(--white)',
          border: '1px solid var(--line)',
          borderRadius: '12px',
          color: 'var(--muted)'
        }}>
          <RefreshCw size={24} className="animate-spin" style={{ margin: '0 auto 12px auto', display: 'block' }} />
          Loading storage configurations…
        </div>
      ) : (
        <form onSubmit={handleSave}>
          {/* Status Overview Card */}
          <div style={{
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: '12px',
            padding: '20px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '10px',
                  background: 'var(--blue-soft)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--blue)'
                }}>
                  <Cloud size={22} />
                </div>
                <div>
                  <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink)' }}>
                    Active Provider: {provider.toUpperCase()}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--muted)' }}>
                    {config?.isUsingEnvDefault 
                      ? 'Currently operating with server environment defaults (.env).' 
                      : 'Tenant-specific custom storage settings are active.'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '16px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: provider === 's3' ? 'var(--ok-soft)' : 'var(--warn-soft)',
                  color: provider === 's3' ? 'var(--ok)' : 'var(--warn)',
                  border: `1px solid ${provider === 's3' ? 'rgba(15, 138, 95, 0.2)' : 'rgba(178, 106, 0, 0.2)'}`
                }}>
                  {provider === 's3' ? 'AWS S3 Vault Active' : 'Non-S3 Provider'}
                </span>
              </div>
            </div>
          </div>

          {/* Provider Selection */}
          <div style={{
            background: 'var(--white)',
            border: '1px solid var(--line)',
            borderRadius: '12px',
            padding: '24px',
            marginBottom: '24px',
            boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)'
          }}>
            <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink)', marginBottom: '8px' }}>
              Storage Provider Architecture
            </h3>
            <p style={{ fontSize: '13px', color: 'var(--muted)', marginBottom: '20px' }}>
              Choose where learner audio/video recordings and assessment artifacts will be securely archived.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '16px' }}>
              {/* Option: AWS S3 */}
              <label style={{
                border: `2px solid ${provider === 's3' ? 'var(--blue)' : 'var(--line)'}`,
                borderRadius: '10px',
                padding: '16px',
                cursor: 'pointer',
                background: provider === 's3' ? 'var(--blue-soft)' : 'var(--white)',
                transition: 'all 0.15s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink)' }}>Amazon Web Services (S3)</span>
                  <input
                    type="radio"
                    name="provider"
                    value="s3"
                    checked={provider === 's3'}
                    onChange={() => setProvider('s3')}
                    style={{ accentColor: 'var(--blue)' }}
                  />
                </div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.4' }}>
                  High-scale enterprise object vault with AWS SigV4 signed uploads and direct video streaming. (Recommended)
                </div>
              </label>

              {/* Option: Local Storage */}
              <label style={{
                border: `2px solid ${provider === 'local' ? 'var(--blue)' : 'var(--line)'}`,
                borderRadius: '10px',
                padding: '16px',
                cursor: 'pointer',
                background: provider === 'local' ? 'var(--blue-soft)' : 'var(--white)',
                transition: 'all 0.15s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink)' }}>Local Server Storage</span>
                  <input
                    type="radio"
                    name="provider"
                    value="local"
                    checked={provider === 'local'}
                    onChange={() => setProvider('local')}
                    style={{ accentColor: 'var(--blue)' }}
                  />
                </div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.4' }}>
                  Stores video blobs on EC2 server local disk storage (`uploads/recordings`). Intended for local staging.
                </div>
              </label>

              {/* Option: Cloudinary */}
              <label style={{
                border: `2px solid ${provider === 'cloudinary' ? 'var(--blue)' : 'var(--line)'}`,
                borderRadius: '10px',
                padding: '16px',
                cursor: 'pointer',
                background: provider === 'cloudinary' ? 'var(--blue-soft)' : 'var(--white)',
                transition: 'all 0.15s ease'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink)' }}>Cloudinary (Legacy)</span>
                  <input
                    type="radio"
                    name="provider"
                    value="cloudinary"
                    checked={provider === 'cloudinary'}
                    onChange={() => setProvider('cloudinary')}
                    style={{ accentColor: 'var(--blue)' }}
                  />
                </div>
                <div style={{ fontSize: '12px', color: 'var(--muted)', lineHeight: '1.4' }}>
                  Third-party media cloud CDN. Maintained for legacy compatibility.
                </div>
              </label>
            </div>
          </div>

          {/* S3 Details Configuration */}
          {provider === 's3' && (
            <div style={{
              background: 'var(--white)',
              border: '1px solid var(--line)',
              borderRadius: '12px',
              padding: '24px',
              marginBottom: '24px',
              boxShadow: '0 1px 3px rgba(7, 29, 69, 0.04)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <HardDrive size={20} style={{ color: 'var(--navy)' }} />
                <h3 style={{ fontSize: '16px', fontWeight: 700, color: 'var(--ink)', margin: 0 }}>
                  AWS S3 Bucket & Region Details
                </h3>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px', marginBottom: '20px' }}>
                {/* S3 Bucket Name */}
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '6px' }}>
                    S3 Bucket Name <span style={{ color: 'var(--bad)' }}>*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={s3Bucket}
                    onChange={(e) => setS3Bucket(e.target.value)}
                    placeholder="e.g. trainova-media-vault"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--line)',
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  />
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                    Lowercase letters, numbers, and hyphens (e.g. <code>trainova-media-vault</code>)
                  </div>
                </div>

                {/* S3 Region */}
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '6px' }}>
                    AWS Region <span style={{ color: 'var(--bad)' }}>*</span>
                  </label>
                  <select
                    value={s3Region}
                    onChange={(e) => setS3Region(e.target.value)}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--line)',
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    {AWS_REGIONS.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                    AWS data center region where this bucket is provisioned.
                  </div>
                </div>

                {/* Custom Prefix */}
                <div>
                  <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '6px' }}>
                    Custom Path Prefix <span style={{ color: 'var(--muted)', fontWeight: 400 }}>(Optional)</span>
                  </label>
                  <input
                    type="text"
                    value={s3CustomPrefix}
                    onChange={(e) => setS3CustomPrefix(e.target.value)}
                    placeholder="e.g. org-name/ or leave blank for default"
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '8px',
                      border: '1px solid var(--line)',
                      background: 'var(--surface)',
                      color: 'var(--ink)',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  />
                  <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: '4px' }}>
                    Default path format: <code>tenants/{'{tenantId}'}/attempts/{'{attemptId}'}/video/...</code>
                  </div>
                </div>
              </div>

              {/* AWS IAM Credentials Section */}
              <div style={{
                borderTop: '1px solid var(--line)',
                paddingTop: '20px',
                marginTop: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                  <Key size={16} style={{ color: 'var(--blue)' }} />
                  <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--ink)' }}>
                    AWS IAM Credentials (Encrypted with AES-256-GCM)
                  </span>
                </div>

                <div style={{
                  background: 'var(--surface)',
                  border: '1px solid var(--line)',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  fontSize: '13px',
                  color: 'var(--muted)'
                }}>
                  <ShieldCheck size={18} style={{ color: 'var(--ok)', flexShrink: 0 }} />
                  <span>
                    When left blank, Trainova automatically falls back to the server environment defaults (EC2 IAM role or <code>.env</code> file). 
                    Only specify credentials here if this tenant uses its own dedicated AWS IAM account.
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '20px' }}>
                  {/* Access Key ID */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '6px' }}>
                      AWS Access Key ID
                    </label>
                    <input
                      type="text"
                      value={awsAccessKeyId}
                      onChange={(e) => setAwsAccessKeyId(e.target.value)}
                      placeholder="e.g. AKIAIOSFODNN7EXAMPLE"
                      style={{
                        width: '100%',
                        padding: '10px 14px',
                        borderRadius: '8px',
                        border: '1px solid var(--line)',
                        background: 'var(--white)',
                        color: 'var(--ink)',
                        fontSize: '14px',
                        fontWeight: 500
                      }}
                    />
                  </div>

                  {/* Secret Access Key */}
                  <div>
                    <label style={{ display: 'block', fontWeight: 600, fontSize: '13px', color: 'var(--ink)', marginBottom: '6px' }}>
                      AWS Secret Access Key
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showSecretKey ? 'text' : 'password'}
                        value={awsSecretAccessKey}
                        onChange={(e) => setAwsSecretAccessKey(e.target.value)}
                        placeholder="••••••••••••••••••••••••"
                        style={{
                          width: '100%',
                          padding: '10px 42px 10px 14px',
                          borderRadius: '8px',
                          border: '1px solid var(--line)',
                          background: 'var(--white)',
                          color: 'var(--ink)',
                          fontSize: '14px',
                          fontWeight: 500
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecretKey(!showSecretKey)}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: 'var(--muted)',
                          padding: '4px'
                        }}
                      >
                        {showSecretKey ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Test Diagnostic Result Box */}
          {testResult.status !== 'idle' && (
            <div style={{
              background: testResult.status === 'success' ? 'var(--ok-soft)' : (testResult.status === 'error' ? 'var(--bad-soft)' : 'var(--blue-soft)'),
              border: `1px solid ${testResult.status === 'success' ? 'rgba(15, 138, 95, 0.25)' : (testResult.status === 'error' ? 'rgba(194, 59, 59, 0.25)' : 'rgba(13, 79, 189, 0.25)')}`,
              borderRadius: '10px',
              padding: '16px 20px',
              marginBottom: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {testResult.status === 'testing' && <RefreshCw size={20} className="animate-spin" style={{ color: 'var(--blue)' }} />}
                {testResult.status === 'success' && <CheckCircle2 size={20} style={{ color: 'var(--ok)' }} />}
                {testResult.status === 'error' && <AlertCircle size={20} style={{ color: 'var(--bad)' }} />}
                <div>
                  <div style={{ 
                    fontWeight: 600, 
                    fontSize: '14px', 
                    color: testResult.status === 'success' ? 'var(--ok)' : (testResult.status === 'error' ? 'var(--bad)' : 'var(--blue)')
                  }}>
                    {testResult.status === 'testing' && 'Verifying AWS S3 Connection & IAM Permissions…'}
                    {testResult.status === 'success' && 'AWS S3 Connection Verified Successfully'}
                    {testResult.status === 'error' && 'S3 Connection Failed'}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--ink)', marginTop: '2px' }}>
                    {testResult.message || testResult.error}
                  </div>
                </div>
              </div>

              {testResult.latencyMs !== undefined && (
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: 600,
                  background: 'var(--white)',
                  color: 'var(--ok)',
                  border: '1px solid rgba(15, 138, 95, 0.2)'
                }}>
                  ⚡ {testResult.latencyMs}ms
                </span>
              )}
            </div>
          )}

          {/* Sticky Action Footer */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '14px',
            marginTop: '30px',
            borderTop: '1px solid var(--line)',
            paddingTop: '20px'
          }}>
            {provider === 's3' && (
              <button
                type="button"
                onClick={handleTestConnection}
                disabled={testResult.status === 'testing' || isSaving}
                style={{
                  background: 'var(--blue-soft)',
                  color: 'var(--blue)',
                  border: '1px solid transparent',
                  padding: '10px 18px',
                  borderRadius: '8px',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {testResult.status === 'testing' ? <RefreshCw size={16} className="animate-spin" /> : <Server size={16} />}
                Test S3 Connection
              </button>
            )}

            <button
              type="submit"
              disabled={isSaving}
              style={{
                background: 'var(--blue)',
                color: 'var(--white)',
                border: 'none',
                padding: '10px 24px',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 4px rgba(13, 79, 189, 0.2)'
              }}
            >
              {isSaving ? <RefreshCw size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
              {isSaving ? 'Saving…' : 'Save Storage Configuration'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
